import { Minus, Plus, X } from 'lucide-react';
import { type SetStateAction, useCallback, useMemo } from 'react';

import { AutoAssignShiftHeader } from '@apps/web/components/outreaches/outreach_shifts_auto_assign';
import { Button } from '@apps/web/components/ui/button';
import { Checkbox } from '@apps/web/components/ui/checkbox';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@apps/web/components/ui/select';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@apps/web/components/ui/table';
import { useFormatPersonName } from '@apps/web/hooks/use_person_name_format';
import { type AutoAssignCaregiverPolicy } from '@apps/web/lib/auto_assign';
import { rawPersonName } from '@apps/web/lib/caregiver_name';
import {
    formatDateAndTimeWithWeekday,
    formatTime,
} from '@apps/web/lib/date_utils';
import { sortShiftsByStartTime } from '@apps/web/lib/outreach_shift_ordering';
import {
    type Organization,
    type Shift,
} from '@libraries/typescript/api_client';

export type ShiftGroupMode = 'all' | 'any';

export type ShiftGroup = {
    readonly id: string;
    readonly shiftIds: ReadonlyArray<string>;
    readonly groupMode: ShiftGroupMode;
};

export type ShiftGroupsValue = ReadonlyArray<ShiftGroup> | null;

export type SerializedShiftGroupCreate = {
    readonly id: string;
    readonly shift_ids: Array<string>;
    readonly group_mode: ShiftGroupMode;
};

export function groupShiftsByClient(
    shifts: ReadonlyArray<Shift>,
    formatName: (fullName: string | null | undefined) => string,
): ReadonlyMap<
    string,
    { readonly name: string; readonly shifts: ReadonlyArray<Shift> }
> {
    const grouped = new Map<
        string,
        { readonly name: string; readonly shifts: Array<Shift> }
    >();
    for (const shift of shifts) {
        const key =
            shift.client_id || (shift.patient_name || '').trim() || 'Unknown';
        const existing = grouped.get(key);
        if (existing) {
            existing.shifts.push(shift);
        } else {
            grouped.set(key, {
                name: formatName(shift.patient_name) || 'Unknown',
                shifts: [shift],
            });
        }
    }
    return grouped;
}

export function buildGroupsFromClients(
    shifts: ReadonlyArray<Shift>,
    defaultGroupMode: ShiftGroupMode = 'all',
): ReadonlyArray<ShiftGroup> {
    // Only the grouped shifts are read here; the names are not shown.
    const clientGroups = groupShiftsByClient(shifts, rawPersonName);
    return [...clientGroups.values()]
        .filter(({ shifts: groupShifts }) => groupShifts.length > 1)
        .map(({ shifts: groupShifts }) => ({
            id: crypto.randomUUID(),
            shiftIds: sortShiftsByStartTime(groupShifts).map(
                (shift) => shift.id,
            ),
            // Same-client shifts can still require different caregiver roles.
            // Treat that automatic grouping as a soft subset preference rather
            // than manufacturing an impossible all-or-none constraint.
            groupMode:
                defaultGroupMode === 'all' &&
                new Set(
                    groupShifts.flatMap((shift) =>
                        shift.role_required ? [shift.role_required] : [],
                    ),
                ).size > 1
                    ? 'any'
                    : defaultGroupMode,
        }));
}

export function sanitizeShiftGroups(
    groups: ReadonlyArray<ShiftGroup>,
    shifts: ReadonlyArray<Shift>,
): ReadonlyArray<ShiftGroup> {
    const validShiftIds = new Set(shifts.map((shift) => shift.id));

    return groups.flatMap((group) => {
        const shiftIds = Array.from(
            new Set(
                group.shiftIds.filter((shiftId) => validShiftIds.has(shiftId)),
            ),
        );
        if (shiftIds.length <= 1) {
            return [];
        }

        return [
            {
                ...group,
                shiftIds,
            },
        ];
    });
}

export function serializeShiftGroups(
    value: ShiftGroupsValue,
): Array<SerializedShiftGroupCreate> | undefined {
    if (!value || value.length === 0) {
        return undefined;
    }
    return value.map((group) => ({
        id: group.id,
        shift_ids: [...group.shiftIds],
        group_mode: group.groupMode,
    }));
}

type SerializedShiftGroupEdit = Omit<SerializedShiftGroupCreate, 'id'> & {
    readonly id: string | null;
};

export function serializeEditedShiftGroups(
    groups: ReadonlyArray<ShiftGroup>,
    loadedGroups: ReadonlyArray<ShiftGroup>,
): Array<SerializedShiftGroupEdit> {
    // Only a loaded group's id is durable; editor-made ids stay local.
    const loadedIds = new Set(loadedGroups.map((group) => group.id));
    return groups
        .filter((group) => group.shiftIds.length > 1)
        .map((group) => ({
            id: loadedIds.has(group.id) ? group.id : null,
            shift_ids: [...group.shiftIds],
            group_mode: group.groupMode,
        }));
}

type ShiftGroupEditorProps = {
    readonly shifts: ReadonlyArray<Shift>;
    // Only the timezone is read, so embedders (like the calendar selection
    // bar) can pass a narrowed organization instead of the full record.
    readonly organization: Pick<Organization, 'timezone'> | undefined;
    readonly value: ReadonlyArray<ShiftGroup>;
    readonly onChange: (value: ReadonlyArray<ShiftGroup>) => void;
    readonly autoAssignShiftIds?: ReadonlySet<string>;
    readonly autoAssignPolicy?: AutoAssignCaregiverPolicy;
    readonly onAutoAssignShiftIdsChange?: (
        value: SetStateAction<ReadonlySet<string>>,
    ) => void;
    readonly showAutoAssign?: boolean;
    readonly showGroupingControls?: boolean;
};

export function ShiftGroupEditor({
    shifts,
    organization,
    value: groups,
    onChange,
    autoAssignShiftIds,
    autoAssignPolicy,
    onAutoAssignShiftIdsChange,
    showAutoAssign = false,
    showGroupingControls = true,
}: ShiftGroupEditorProps) {
    const { formatName } = useFormatPersonName();
    const timezone = organization?.timezone || 'UTC';
    const isMultiShift = shifts.length > 1;
    const getClientLabel = useCallback(
        (shift: Shift) => formatName(shift.patient_name) || 'Unknown client',
        [formatName],
    );

    const shiftById = useMemo(() => {
        const map = new Map<string, Shift>();
        for (const shift of shifts) {
            map.set(shift.id, shift);
        }
        return map;
    }, [shifts]);

    const groupByShiftId = useMemo(() => {
        const map = new Map<string, ShiftGroup>();
        for (const group of groups) {
            for (const shiftId of group.shiftIds) {
                map.set(shiftId, group);
            }
        }
        return map;
    }, [groups]);

    const ungroupedShifts = useMemo(() => {
        const assignedIds = new Set(groups.flatMap((group) => group.shiftIds));
        return shifts.filter((shift) => !assignedIds.has(shift.id));
    }, [shifts, groups]);

    const handleDeleteGroup = useCallback(
        (groupId: string) => {
            onChange(groups.filter((group) => group.id !== groupId));
        },
        [groups, onChange],
    );

    const handleMoveShift = useCallback(
        (shiftId: string, targetGroupId: string | 'ungrouped' | 'new') => {
            // Remove shift from its current group
            const updatedGroups = groups
                .map((group) => ({
                    ...group,
                    shiftIds: group.shiftIds.filter((id) => id !== shiftId),
                }))
                .filter((group) => group.shiftIds.length > 0);

            if (targetGroupId === 'new') {
                updatedGroups.push({
                    id: crypto.randomUUID(),
                    shiftIds: [shiftId],
                    groupMode: 'all',
                });
            } else if (targetGroupId !== 'ungrouped') {
                const target = updatedGroups.find(
                    (g) => g.id === targetGroupId,
                );
                if (target) {
                    target.shiftIds.push(shiftId);
                    target.shiftIds.sort((a, b) => {
                        const sa = shiftById.get(a);
                        const sb = shiftById.get(b);
                        if (!sa || !sb) {
                            return 0;
                        }
                        if (sa.shift_start_time === sb.shift_start_time) {
                            return a.localeCompare(b);
                        }
                        return sa.shift_start_time.localeCompare(
                            sb.shift_start_time,
                        );
                    });
                }
            }

            onChange(updatedGroups);
        },
        [groups, onChange, shiftById],
    );

    const handleGroupModeChange = useCallback(
        (groupId: string, mode: ShiftGroupMode) => {
            onChange(
                groups.map((group) =>
                    group.id === groupId
                        ? { ...group, groupMode: mode }
                        : group,
                ),
            );
        },
        [groups, onChange],
    );

    const toggleAutoAssign = useCallback(
        (shiftId: string, group: ShiftGroup | null, checked: boolean) => {
            onAutoAssignShiftIdsChange?.((prev) => {
                const next = new Set(prev);
                // For "all" groups, toggle every shift in the group.
                const ids =
                    group?.groupMode === 'all' ? group.shiftIds : [shiftId];
                for (const id of ids) {
                    if (checked) {
                        next.add(id);
                    } else {
                        next.delete(id);
                    }
                }
                return next;
            });
        },
        [onAutoAssignShiftIdsChange],
    );

    const renderShiftRow = (
        shift: Shift,
        currentGroupId: string | null,
        group: ShiftGroup | null,
    ) => (
        <TableRow key={shift.id}>
            <TableCell>
                <div className="font-medium">{getClientLabel(shift)}</div>
                <div className="text-muted-foreground text-sm">
                    {formatDateAndTimeWithWeekday(
                        shift.shift_start_time,
                        timezone,
                    )}{' '}
                    - {formatTime(shift.shift_end_time, timezone)}
                </div>
            </TableCell>
            <TableCell>{shift.patient_city || '\u2014'}</TableCell>
            {isMultiShift && showGroupingControls && (
                <TableCell className="w-[200px]">
                    <div className="flex items-center gap-1.5">
                        <Select
                            value={currentGroupId ?? 'ungrouped'}
                            onValueChange={(value) =>
                                handleMoveShift(shift.id, value)
                            }
                        >
                            <SelectTrigger className="h-8 w-[120px] text-xs">
                                <SelectValue placeholder="None" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="ungrouped">None</SelectItem>
                                {groups.map((g, index) => (
                                    <SelectItem key={g.id} value={g.id}>
                                        Group {index + 1}
                                    </SelectItem>
                                ))}
                                <SelectItem value="new">+ New group</SelectItem>
                            </SelectContent>
                        </Select>
                        <Button
                            type="button"
                            variant="outline"
                            size="icon"
                            className="size-7 shrink-0 rounded-full"
                            aria-label="Create new group"
                            onClick={() => handleMoveShift(shift.id, 'new')}
                        >
                            <Plus className="h-3.5 w-3.5" />
                        </Button>
                        {currentGroupId && (
                            <Button
                                type="button"
                                variant="outline"
                                size="icon"
                                className="size-7 shrink-0 rounded-full"
                                aria-label="Remove from group"
                                onClick={() =>
                                    handleMoveShift(shift.id, 'ungrouped')
                                }
                            >
                                <Minus className="h-3.5 w-3.5" />
                            </Button>
                        )}
                    </div>
                </TableCell>
            )}
            {showAutoAssign && (
                <TableCell className="w-[80px] text-center">
                    <Checkbox
                        checked={autoAssignShiftIds?.has(shift.id) ?? false}
                        onCheckedChange={(checked) =>
                            toggleAutoAssign(shift.id, group, checked === true)
                        }
                    />
                </TableCell>
            )}
        </TableRow>
    );

    const tableHeader = (
        <TableHeader>
            <TableRow>
                <TableHead />
                <TableHead>Location</TableHead>
                {isMultiShift && showGroupingControls && (
                    <TableHead>Group</TableHead>
                )}
                {showAutoAssign && (
                    <TableHead className="w-[80px] text-center">
                        <AutoAssignShiftHeader policy={autoAssignPolicy} />
                    </TableHead>
                )}
            </TableRow>
        </TableHeader>
    );

    if (!showGroupingControls) {
        return (
            <div className="rounded-md border">
                <Table>
                    {tableHeader}
                    <TableBody>
                        {shifts.map((shift) => {
                            const group = groupByShiftId.get(shift.id) ?? null;
                            return renderShiftRow(
                                shift,
                                group?.id ?? null,
                                group,
                            );
                        })}
                    </TableBody>
                </Table>
            </div>
        );
    }

    return (
        <div className="space-y-4">
            {groups.map((group, index) => (
                <div key={group.id} className="space-y-2">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                            <span className="text-sm font-medium">
                                Group {index + 1}
                                <span className="text-muted-foreground ml-1 font-normal">
                                    ({group.shiftIds.length}{' '}
                                    {group.shiftIds.length === 1
                                        ? 'shift'
                                        : 'shifts'}
                                    )
                                </span>
                            </span>
                            <Select
                                value={group.groupMode}
                                onValueChange={(value) =>
                                    handleGroupModeChange(
                                        group.id,
                                        value as ShiftGroupMode,
                                    )
                                }
                            >
                                <SelectTrigger className="h-7 w-[140px] text-xs">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">
                                        All or none
                                    </SelectItem>
                                    <SelectItem value="any">Any</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6"
                            onClick={() => handleDeleteGroup(group.id)}
                        >
                            <X className="h-3 w-3" />
                        </Button>
                    </div>
                    {group.shiftIds.length === 1 && (
                        <p className="rounded bg-yellow-50 px-2 py-1 text-xs text-yellow-700">
                            Groups with a single shift will be treated as
                            ungrouped.
                        </p>
                    )}
                    {group.shiftIds.length > 0 ? (
                        <div className="rounded-lg border">
                            <Table>
                                {tableHeader}
                                <TableBody>
                                    {group.shiftIds.map((shiftId) => {
                                        const shift = shiftById.get(shiftId);
                                        if (!shift) {
                                            return null;
                                        }
                                        return renderShiftRow(
                                            shift,
                                            group.id,
                                            group,
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </div>
                    ) : (
                        <p className="text-muted-foreground rounded-lg border p-3 text-center text-sm">
                            No shifts assigned. Use the dropdown below to move
                            shifts into this group.
                        </p>
                    )}
                </div>
            ))}
            {ungroupedShifts.length > 0 && (
                <div className="space-y-2">
                    <span className="text-sm font-medium">
                        Not grouped
                        <span className="text-muted-foreground ml-1 font-normal">
                            ({ungroupedShifts.length}{' '}
                            {ungroupedShifts.length === 1 ? 'shift' : 'shifts'})
                        </span>
                    </span>
                    <div className="rounded-lg border">
                        <Table>
                            {tableHeader}
                            <TableBody>
                                {ungroupedShifts.map((shift) =>
                                    renderShiftRow(shift, null, null),
                                )}
                            </TableBody>
                        </Table>
                    </div>
                </div>
            )}
        </div>
    );
}
